import { Button } from "@chakra-ui/button";
import { Box, HStack } from "@chakra-ui/layout";
import { FormikProps, withFormik } from "formik";
import React, { useEffect } from "react";
import { AdminApiClient } from "../api/adminApiClient";
import { CherryHubAdminApi } from "../api/adminApiModels";
import { ChakraReactSelectField } from "../components/fields/SelectField/ChakraReactSelectField";
import { SwitchField } from "../components/fields/SwitchField/SwitchField";
import { FormStack } from "../components/FormStack/FormStack";
import { AdminApiEnumConfigurations } from "../enums/AdminApiEnumConfigurations";
import { useAdminApiRequest } from "../hooks/useApiRequest";

interface MembershipLoyaltyConfigurationFormProps {
  apiClient: AdminApiClient;
  businessId: string;
  integration: CherryHubAdminApi.Integration | null;
  isDisabled?: boolean;
  onCancel: () => void;
  onSuccess: (integration: CherryHubAdminApi.Integration) => void;
  onFailure: (message?: string) => void;
}

interface MembershipLoyaltyConfigurationFormValues {
  memberKey?:
    | "MemberKey"
    | "MembershipNumber"
    | "BadgeNumber"
    | "CardNumber"
    | "Id";
  pointsAwardEnabled?: boolean;
  pointsRedeemEnabled?: boolean;
}

const InnerForm = ({
  isSubmitting,
  onCancel,
}: MembershipLoyaltyConfigurationFormProps &
  FormikProps<MembershipLoyaltyConfigurationFormValues>) => {
  const { data: enums, isLoading: enumLoading } = useAdminApiRequest(
    (apiClient) =>
      apiClient.getEnumConfigurations(
        AdminApiEnumConfigurations.MemberKeyField
      ),
    []
  );
  return (
    <>
      <Box w="100%" maxW="800px" minH="500px" alignSelf="flex-start">
        <FormStack>
          <ChakraReactSelectField
            horizontal
            allowEmpty={true}
            label="Member Key"
            name="memberKey"
            selectedOptionColor="blue"
            isLoading={enumLoading}
            options={
              enums?.map((item) => {
                return {
                  label: item.Value!,
                  value: item.Key!,
                };
              }) || []
            }
          />

          <SwitchField
            horizontal
            label="Points Award Enabled"
            name="pointsAwardEnabled"
          />

          <SwitchField
            horizontal
            label="Points Redeem Enabled"
            name="pointsRedeemEnabled"
          />

          <HStack>
            <Button
              disabled={isSubmitting}
              type="submit"
              colorScheme="cherryButton"
            >
              Save
            </Button>
            <Button disabled={isSubmitting} onClick={onCancel}>
              Cancel
            </Button>
          </HStack>
        </FormStack>
      </Box>
    </>
  );
};

export const MembershipLoyaltyConfigurationForm = withFormik<
  MembershipLoyaltyConfigurationFormProps,
  MembershipLoyaltyConfigurationFormValues
>({
  mapPropsToValues: ({ integration }) => {
    return {
      memberKey: integration?.MembershipLoyaltyServiceConfiguration?.MemberKey,
      pointsAwardEnabled:
        integration?.MembershipLoyaltyServiceConfiguration
          ?.PointsAwardEnabled ?? true,
      pointsRedeemEnabled:
        integration?.MembershipLoyaltyServiceConfiguration
          ?.PointsRedeemEnabled ?? true,
    };
  },

  handleSubmit: async (values, { props }) => {
    // Because we use the same endpoint with `Edit` detail please make sure we include anything from the integration model
    const integrationData: Partial<CherryHubAdminApi.IntegrationUpdate> = {
      ...props.integration,
      MembershipLoyaltyServiceConfiguration: {
        MemberKey: values.memberKey,
        PointsAwardEnabled: values.pointsAwardEnabled,
        PointsRedeemEnabled: values.pointsRedeemEnabled,
      },
    };
    const result = await props.apiClient.updateBusinessIntegration(
      props.businessId,
      props.integration?.id!,
      integrationData
    );
    if (result.ok) {
      props.onSuccess(result.data);
    } else {
      props.onFailure(result.message);
    }
  },
})(InnerForm);
